/**
 * SU client bootstrap — v0M2.4a polished render + v0M3.7 start menu +
 * v0M3.7a ownership tint + v0M3.7b sim tick driver + speed HUD.
 *
 * Flow:
 *   1. Load map + atlas (concurrent).
 *   2. Mount menu overlay (Main → Mode Select → Standard Config).
 *   3. On "Start", call `bootstrapScenario`, mount the SimDriver at 1×
 *      autoplay, mount the speed HUD, wire Space to togglePause, and
 *      start the render loop that ticks the sim + redraws each frame.
 */

import { loadMapFromUrl } from './map/map-loader';
import { createCamera, clampCameraToMap, pxPerCell } from './map/camera';
import { bakeAllLayers, drawFrame } from './map/renderer';
import type { RenderState } from './map/renderer';
import { loadTileAtlas } from './map/tile-atlas';
import { loadRiverTileAtlas } from './map/river-tile-atlas';
import { mountInput, applyPanFromInput } from './input';
import { runRenderProfile } from './render-profile';
import { mountMenus } from './menu';
import { loadSettings, subscribeSettings } from './settings-storage';
import { applyAudioSettings } from './audio';
import { mountInGameMenu } from './menu/in-game-menu';
import type { InGameMenuHandle } from './menu/in-game-menu';
import { mountSaveSlotPicker } from './menu/save-slot-picker';
import type { SaveSlotPickerHandle } from './menu/save-slot-picker';
import { mountSettingsModal } from './menu/settings-modal';
import type { SettingsModalHandle } from './menu/settings-modal';
import { deleteSlot, readSlot, writeSlot } from './save-storage';
import { startAutosaveLoop } from './autosave';
import { SAVE_SCHEMA_VERSION, deserialiseSavedGame, serialiseSavedGame } from '../sim/save';
import type { SavedGame } from '../sim/save';
import { mountSpeedHud } from './menu/speed-hud';
import { mountResourceHud } from './menu/resource-hud';
import { mountDevConsole } from './menu/dev-console';
import { mountArmyInfoPanel } from './menu/army-info-panel';
// MFix12.5b (2026-07-27) — cohort transfer modal.
import { mountCohortTransferModal } from './menu/cohort-transfer-modal';
import { mountVictoryBanner } from './menu/victory-banner';
import { mountSettlementPanel } from './menu/settlement-panel';
import { mountMinorSettlementPanel } from './menu/minor-settlement-panel';
import { mountFortPanel } from './menu/fort-panel';
import { mountInfiltrationPanel } from './menu/infiltration-panel';
import { mountBattleCommandModal } from './menu/battle-command-modal';
import { mountKnowledgePanel } from './menu/knowledge-panel';
import { mountTopBar } from './menu/top-bar';
import { bootstrapScenario } from '../sim/scenario';
import { getFactionByName } from '../data/factions';
import { toFactionId, TICKS_PER_GAME_DAY } from '../sim/state';
import { buildRegionGraph } from '../sim/pathfind';
import { createSimDriver } from './sim-driver';
import type { SimSpeed } from './sim-driver';
import {
  createSelectionState,
  selectArmy,
  clearArmySelection,
  selectSpy,
  clearSpySelection,
  openCityPanel,
  closeCityPanel,
  openBattleCommand,
  closeBattleCommand,
  openKnowledgePanel,
  closeKnowledgePanel,
  openInfiltrationPanel,
  closeInfiltrationPanel,
  openMinorSettlementPanel,
  closeMinorSettlementPanel,
  openFortPanel,
  closeFortPanel,
  openTransferModal,
  closeTransferModal,
  clearAll as clearSelection,
} from './selection';
import { findAnySettlementAtScreen, findArmyAtScreen, findBattleAtScreen, findCityProvinceAtScreen, findFortAtScreen, findMinorSettlementAtScreen, findSiegeAtScreen, findSpyAtScreen, screenToCell } from './hit-test';
import { openSiegeCommandMenu } from './menu/siege-command-menu';
import type { SiegeCommandMenuHandle } from './menu/siege-command-menu';
import type { LoadedMap } from './map/map-loader';
import type { Scenario } from '../sim/scenario';
import type { GameState } from '../sim/state';
import type { SimContext } from '../sim/sim-context';
import { hierarchicalPath } from '../sim/pathfind';
import { computeBridgeCells } from '../sim/build';
import { WORLD_BRIDGE_MOVE_MULTIPLIER } from '../sim/apply-commands';

const BOOTSTRAP_ERROR_ID = 'bootstrap-error';
const CANVAS_ID = 'game-canvas';
const SAMPLE_MAP_URL = '/maps/karth-sample.json';

/**
 * MFix5+fix6 (2026-07-23) — cheap signature of every province's owner.
 * Recomputed per frame; if different from `bakedOwnershipSig`, the
 * political tint rebakes. O(provinces) per frame — ~100 comparisons,
 * trivial cost.
 */
function computeOwnershipSignature(state: { provinces: ReadonlyMap<{ readonly __brand: 'ProvinceId' } & number, { ownerFactionId: unknown }> }): string {
  const parts: string[] = [];
  for (const [pid, province] of state.provinces) {
    const pidn = pid as unknown as number;
    const owner = province.ownerFactionId as unknown as number | null;
    parts.push(`${pidn}:${owner ?? 'null'}`);
  }
  parts.sort();
  return parts.join(',');
}

/**
 * v0M5.4 (2026-07-25) — Cheap check for LMB routing: does the player
 * have at least one own spy inside `settlementId`? Used before opening
 * the infiltration panel.
 */
function hasOwnSpyInside(
  state: import('../sim/state').GameState,
  settlementId: import('../sim/state').SettlementId,
  playerFactionId: import('../sim/state').FactionId,
): boolean {
  if (!state.spies) return false;
  for (const spy of state.spies.values()) {
    if (spy.ownerFactionId !== playerFactionId) continue;
    if (spy.insideSettlementId === settlementId) return true;
  }
  return false;
}

async function bootstrap(): Promise<void> {
  const el = document.getElementById(CANVAS_ID);
  if (!(el instanceof HTMLCanvasElement)) {
    fail(`Bootstrap error: #${CANVAS_ID} not found in DOM.`);
    throw new Error(`SU bootstrap failure — canvas element missing`);
  }
  // Bind the narrowed reference to a strictly-typed const so the type
  // survives into nested closures below (TS re-widens the outer const
  // captured in `frame` otherwise).
  const canvas: HTMLCanvasElement = el;

  fitCanvasToWindow(canvas);
  window.addEventListener('resize', () => fitCanvasToWindow(canvas));

  // v0M15.4 — Apply persisted audio settings at boot and re-apply on
  // every subsequent change. The audio context itself is created
  // lazily by battle-effects's first playSfx call; applyAudioSettings
  // is a no-op until then, then becomes live.
  applyAudioSettings(loadSettings());
  subscribeSettings((s) => applyAudioSettings(s));

  const ctx2d = canvas.getContext('2d');
  if (!ctx2d) {
    fail('Bootstrap error: Canvas 2D context unavailable in this browser.');
    throw new Error('SU bootstrap failure — 2D context missing');
  }
  const ctx: CanvasRenderingContext2D = ctx2d;

  const [map, atlas, riverAtlas] = await Promise.all([
    loadMapFromUrl(SAMPLE_MAP_URL),
    loadTileAtlas(),
    loadRiverTileAtlas(),
  ]);
  // eslint-disable-next-line no-console
  console.info(
    `[SU] map loaded: ${map.name}, ${map.width}×${map.height}, ${map.provinces.length} provinces`,
  );

  const camera = createCamera(map.width, map.height);

  // v0M2.8 spike — render profile takes over the loop when ?profile=1 is
  // set on the URL. Skips input mount + normal frame loop; runs a scripted
  // bake/draw sweep across the three zoom steps, prints a console.table
  // summary + surfaces a JSON snapshot download.
  const params = new URLSearchParams(window.location.search);
  if (params.get('profile') === '1') {
    await runRenderProfile({ ctx, map, atlas, camera });
    return;
  }

  // v0M15.5 — If the previous session's in-game menu triggered a Load,
  // the picked SavedGame is stashed in sessionStorage under
  // `su:pending-load`. Read it now; if present, boot straight into the
  // loaded game and skip the main-menu mount. Cleared regardless of
  // success so a refresh doesn't loop forever.
  const pendingLoad = consumePendingLoad();
  if (pendingLoad !== null) {
    startGame({
      ctx, canvas, map, atlas, riverAtlas, camera,
      scenario: pendingLoad.scenario,
      preBootstrappedState: pendingLoad.state,
    });
    return;
  }

  // v0M3.7 — mount menu overlay first. The render loop starts only after
  // the player clicks Start on the Standard Config screen.
  // v0M15.3 — Load Game path bypasses Standard Config: the saved
  // GameState is dropped into startGame directly, and saved.scenario is
  // used verbatim so playerFactionId / victoryCondition / slot roles all
  // resolve identically to a fresh Start.
  mountMenus(document.body, {
    onStart: (scenario) => {
      startGame({ ctx, canvas, map, atlas, riverAtlas, camera, scenario });
    },
    onLoad: (saved) => {
      startGame({
        ctx, canvas, map, atlas, riverAtlas, camera,
        scenario: saved.scenario,
        preBootstrappedState: saved.state,
      });
    },
  });

  // eslint-disable-next-line no-console
  console.info(
    '%cSU client bootstrap',
    'color:#e6dcc4;background:#0e0b06;padding:2px 6px;border-radius:2px',
    'v0M3.7 start menu — click New Game to begin.',
  );
}

interface StartGameArgs {
  readonly ctx: CanvasRenderingContext2D;
  readonly canvas: HTMLCanvasElement;
  readonly map: LoadedMap;
  readonly atlas: Awaited<ReturnType<typeof loadTileAtlas>>;
  readonly riverAtlas: Awaited<ReturnType<typeof loadRiverTileAtlas>>;
  readonly camera: ReturnType<typeof createCamera>;
  readonly scenario: Scenario;
  /** v0M15.3 — if supplied (Load Game path), skip bootstrapScenario and
   *  use this GameState directly. The scenario is still required so
   *  playerFactionId + SimContext.victoryCondition resolve identically
   *  to a fresh Start. */
  readonly preBootstrappedState?: GameState;
}

/**
 * Bootstrap the sim from the Scenario emitted by the menu + start the
 * client render loop. v0M3.7b adds the sim tick driver — from Start
 * onward the sim is ticking at 5 Hz × speed multiplier (default 1×).
 * Space toggles pause/resume; the speed HUD shows current day + speed
 * and lets the player click into Pause / 1× / 2× / 4×.
 *
 * v0M15.3 — when `preBootstrappedState` is supplied the Load Game path
 * uses it verbatim; otherwise we bootstrap fresh from the scenario.
 */
function startGame(args: StartGameArgs): void {
  const { ctx, canvas, map, atlas, riverAtlas, camera, scenario, preBootstrappedState } = args;

  const initialState = preBootstrappedState ?? bootstrapScenario(scenario, map);
  // eslint-disable-next-line no-console
  console.info(
    `[SU] scenario bootstrapped: ${initialState.factions.size} factions, ${initialState.provinces.size} provinces, ` +
    `${initialState.armies.size} armies, ${initialState.commanders.size} commanders, ${initialState.cohorts.size} cohorts, ${initialState.settlements.size} cities.`,
  );

  // MFix5+fix (2026-07-23): dev diagnostic dump for playtest bug-hunting.
  // Lists every city — id, cell, ownerFactionId, ownerTeamId, garrison
  // count — so Michael can eyeball whether the neutral/enemy capital he's
  // trying to attack even exists in `state.settlements`. If it doesn't, the
  // sieges system will never see it (bug is in scenario boot, not sieges).
  {
    // eslint-disable-next-line no-console
    const rows: Array<{ settlementId: number; cell: string; provinceId: number; ownerFactionId: number | null; ownerTeamId: number | null; garrison: number }> = [];
    for (const [settlementId, city] of initialState.settlements) {
      const province = initialState.provinces.get(city.provinceId);
      const ownerFactionId = province?.ownerFactionId ?? null;
      const ownerFaction = ownerFactionId !== null ? initialState.factions.get(ownerFactionId) : null;
      rows.push({
        settlementId: settlementId as unknown as number,
        cell: `(${city.cell.x}, ${city.cell.y})`,
        provinceId: city.provinceId as unknown as number,
        ownerFactionId: ownerFactionId as unknown as number | null,
        ownerTeamId: ownerFaction?.teamId ?? null,
        garrison: city.garrisonCohortIds.length,
      });
    }
    // eslint-disable-next-line no-console
    console.info('[SU] cities at boot:', rows);
    // eslint-disable-next-line no-console
    console.info('[SU] factions at boot:', Array.from(initialState.factions.values()).map((f) => ({
      id: f.id as unknown as number,
      teamId: f.teamId,
      isAI: f.isAI,
    })));
  }


  // Player faction id — menu validation guarantees exactly one Player slot.
  const playerSlot = scenario.slots.find((s) => s.role === 'player');
  const playerFactionId = playerSlot
    ? toFactionId(getFactionByName(playerSlot.factionName).id)
    : null;

  // Build SimContext once — pathfinder region graph derived from the
  // loaded map's terrain grid. Reused for every tick.
  const context: SimContext = {
    terrain: map,
    regionGraph: buildRegionGraph(map),
    victoryCondition: scenario.victoryCondition,
    // v0M3.11: wire the loaded map's province mask so cohort recovery
    // (economy.ts stepCohortRecovery) and recruit border-validation
    // (apply-commands.ts) can identify friendly / recruiting provinces.
    provinceMap: map.provinceMap,
  };

  // Sim driver — starts at 1× autoplay per Michael's v0M3.7b lock.
  const driver = createSimDriver({
    initialState,
    context,
    initialSpeed: 1,
  });

  // MFix5+fix (2026-07-23): expose sim state on window for
  // live-console inspection. Michael can type `__su.settlements()`,
  // `__su.armies()`, or `__su.nearCity()` in the console to inspect
  // sim state during a playtest.
  const win = window as unknown as { __su?: unknown };
  win.__su = {
    driver,
    map,
    context,
    state: () => driver.getState(),
    armies: () => {
      const s = driver.getState();
      const out: Array<Record<string, unknown>> = [];
      for (const [armyId, army] of s.armies) {
        const faction = s.factions.get(army.ownerFactionId);
        out.push({
          armyId: armyId as unknown as number,
          factionId: army.ownerFactionId as unknown as number,
          teamId: faction?.teamId ?? null,
          pos: `(${army.position.x.toFixed(1)}, ${army.position.y.toFixed(1)})`,
          currentBattleId: army.currentBattleId as unknown as number | null,
        });
      }
      return out;
    },
    cities: () => {
      const s = driver.getState();
      const out: Array<Record<string, unknown>> = [];
      for (const [settlementId, city] of s.settlements) {
        const province = s.provinces.get(city.provinceId);
        const ownerFactionId = province?.ownerFactionId ?? null;
        const ownerFaction = ownerFactionId !== null ? s.factions.get(ownerFactionId) : null;
        out.push({
          settlementId: settlementId as unknown as number,
          cell: `(${city.cell.x}, ${city.cell.y})`,
          provinceId: city.provinceId as unknown as number,
          ownerFactionId: ownerFactionId as unknown as number | null,
          ownerTeamId: ownerFaction?.teamId ?? null,
          garrison: city.garrisonCohortIds.length,
          siege: city.siegeState === null
            ? '—'
            : `sieger=${city.siegeState.siegingArmyId} days=${city.siegeState.daysRemaining.toFixed(1)}`,
        });
      }
      return out;
    },
    nearCity: (cityCell?: { x: number; y: number }) => {
      const s = driver.getState();
      for (const city of s.settlements.values()) {
        if (cityCell && (cityCell.x !== city.cell.x || cityCell.y !== city.cell.y)) continue;
        // eslint-disable-next-line no-console
        console.log(`[city ${city.id as unknown as number} @ (${city.cell.x},${city.cell.y})]`);
        for (const army of s.armies.values()) {
          const dx = Math.abs(army.position.x - city.cell.x);
          const dy = Math.abs(army.position.y - city.cell.y);
          const cheb = Math.max(dx, dy);
          if (cheb > 5) continue;
          const faction = s.factions.get(army.ownerFactionId);
          // eslint-disable-next-line no-console
          console.log(`  army ${army.id as unknown as number} faction=${army.ownerFactionId as unknown as number} team=${faction?.teamId} pos=(${army.position.x.toFixed(1)},${army.position.y.toFixed(1)}) cheb=${cheb.toFixed(1)} currentBattleId=${army.currentBattleId as unknown as number | null}`);
        }
      }
    },
  };
  // eslint-disable-next-line no-console
  console.info('[SU] dev hook installed. Type `__su.settlements()`, `__su.armies()`, or `__su.nearCity()` in the console.');

  // Speed HUD (top-right) + Resource HUD (top-left) + Victory banner (hidden until game ends).
  // v0M15.5-fix (2026-07-29): the "Menu" button on the speed HUD used
  // to short-circuit to confirm+reload; now it opens the in-game menu
  // (Save / Load / Settings / Return to Main / Exit) — same entry point
  // as pressing Escape.
  const speedHud = mountSpeedHud(document.body, {
    onSetSpeed: (next) => driver.setSpeed(next),
    onOpenMenu: () => openGameMenu(),
  });
  const resourceHud = mountResourceHud(document.body, playerFactionId);
  // MFix7.4 (2026-07-23) — Dev console (toggle with ~).
  mountDevConsole({ root: document.body, driver, playerFactionId });
  // v0M15.6 — Post-game 3-button banner. Return to Main + Load Previous Save
  // route through the same handoff paths as the in-game menu so they behave
  // identically (confirm + reload; picker → sessionStorage handoff + reload).
  // Continue Watching is handled internally by the banner (dismiss + let the
  // sim stay paused; player can un-pause via the speed HUD).
  //
  // The load-picker handle for the post-game Load flow is separate from the
  // in-game menu's picker handle since these two paths never coexist (menu is
  // closed while the banner is up, and vice versa) — no collision.
  let postGameLoadPicker: SaveSlotPickerHandle | null = null;
  const victoryBanner = mountVictoryBanner(document.body, {
    onReturnToMain: () => {
      sessionStorage.removeItem(PENDING_LOAD_KEY);
      window.location.reload();
    },
    onLoadPreviousSave: () => {
      if (postGameLoadPicker !== null) return;
      postGameLoadPicker = mountSaveSlotPicker(document.body, 'load', {
        onLoadFromSlot: (id) => {
          const saved = readSlot(id);
          if (saved === null) {
            window.alert(`Load failed — save "${id}" is missing or incompatible.`);
            return;
          }
          loadInPlaceViaReload(saved);
        },
        onDeleteSlot: (id) => {
          deleteSlot(id);
          postGameLoadPicker?.refresh();
        },
        onCancel: () => {
          if (postGameLoadPicker !== null) {
            postGameLoadPicker.destroy();
            postGameLoadPicker = null;
          }
        },
      });
    },
  });

  // v0M3.10 — selection state + city panel.
  const selection = createSelectionState();

  // v0M8 (2026-07-23) — Army info panel (top-left when player army selected).
  // MFix8.4 (2026-07-24) — X-button onClose clears the army selection so
  // the panel actually stays closed (sync() checks selectedArmyId each frame).
  // v0M7.5 (2026-07-25) — pending build-order location-picker state.
  // Set by the army-info-panel Build X button; consumed by the next
  // canvas LMB (translates click → target cell, dispatches
  // set_army_build_order, clears). Cleared by Escape.
  let pendingBuildOrder: { armyId: import('../sim/state').ArmyId; kind: import('../sim/state').WorldStructureKind } | null = null;
  // v0M7.5b (2026-07-26) — Road polyline picker state. Multi-point:
  // each LMB appends a waypoint; RMB or Enter commits (pathfind
  // between adjacent waypoints, aggregate cell list, dispatch
  // set_army_build_order with kind='road' + targetCell = first cell +
  // roadPath = remaining). Escape cancels.
  let pendingRoadPolyline: { armyId: import('../sim/state').ArmyId; waypoints: { x: number; y: number }[] } | null = null;
  const armyInfoPanel = mountArmyInfoPanel(document.body, playerFactionId, {
    onClose: () => clearArmySelection(selection),
    // v0M10.2 (2026-07-24) — attachments picker in the panel dispatches
    // add_army_attachment via this callback.
    onIssue: (cmd) => driver.enqueue(cmd),
    // v0M7.5 (2026-07-25) — Build X button enters location-picker mode.
    // v0M7.5b (2026-07-26) — Road routes to polyline picker instead of
    // single-cell picker (different UX per MAP.md §9.1).
    onStartBuildOrder: (armyId, kind) => {
      if (kind === 'road') {
        pendingRoadPolyline = { armyId, waypoints: [] };
        pendingBuildOrder = null;
      } else {
        pendingBuildOrder = { armyId, kind };
        pendingRoadPolyline = null;
      }
      canvas.style.cursor = 'crosshair';
    },
    // MFix12.5b (2026-07-27) — Transfer Cohort button opens the modal
    // against the selected army as the source.
    onOpenCohortTransfer: (armyId) => {
      openTransferModal(selection, armyId);
    },
  });

  // MFix12.5b (2026-07-27) — cohort transfer modal.
  const cohortTransferModal = mountCohortTransferModal(document.body, {
    onIssue: (cmd) => driver.enqueue(cmd),
    onClose: () => closeTransferModal(selection),
  });
  // MFix5.4 — siege command menu handle (null when no menu open).
  let siegeMenu: SiegeCommandMenuHandle | null = null;
  const settlementPanel = mountSettlementPanel(document.body, {
    onIssue: (cmd) => driver.enqueue(cmd),
    onClose: () => closeCityPanel(selection),
  });

  // v0M9 (2026-07-27) — minor settlement pillage/repair panel. Opens
  // on LMB of a player-owned minor; the province-scoped settlement
  // panel above still handles capitals.
  const minorSettlementPanel = mountMinorSettlementPanel(document.body, {
    onIssue: (cmd) => driver.enqueue(cmd),
    onClose: () => closeMinorSettlementPanel(selection),
  });

  // v0M7.4d+garrison.3 (2026-07-27) — Fort recruit panel. Opens on
  // LMB of a player-owned Fort. Handles Garrison recruit via the
  // v0M7.4d+garrison sim path.
  const fortPanel = mountFortPanel(document.body, {
    onIssue: (cmd) => driver.enqueue(cmd),
    onClose: () => closeFortPanel(selection),
  });

  // v0M5.4 (2026-07-25) — infiltration panel opens when LMB clicks a
  // settlement with an own spy inside.
  const infiltrationPanel = mountInfiltrationPanel(document.body, {
    onIssue: (cmd) => driver.enqueue(cmd),
    onClose: () => closeInfiltrationPanel(selection),
  });

  // v0M9.5 (2026-07-24) — Knowledge modal + top-bar button.
  const knowledgePanel = mountKnowledgePanel(document.body, {
    onIssue: (cmd) => driver.enqueue(cmd),
    onClose: () => closeKnowledgePanel(selection),
  });
  mountTopBar(document.body, {
    onOpenKnowledge: () => openKnowledgePanel(selection),
  });

  // v0M3.5 — battle-command modal for player-elective "Take command."
  const battleModal = mountBattleCommandModal(document.body, {
    onIssue: (cmd) => driver.enqueue(cmd),
    onClose: () => {
      if (selection.openBattleCommandBattleId !== null && playerFactionId !== null) {
        driver.enqueue({
          type: 'exit_battle_command',
          tick: driver.getState().tick,
          playerFactionId,
          battleId: selection.openBattleCommandBattleId,
        });
      }
      closeBattleCommand(selection);
    },
  });

  // v0M15.5 — In-game menu + autosave loop.
  //
  // getSavedGame() constructs a SavedGame from live driver state on
  // demand. Used by both the manual Save flow (in-game menu → Save)
  // and the autosave loop.
  const getSavedGame = (): SavedGame => ({
    schemaVersion: SAVE_SCHEMA_VERSION,
    scenario,
    state: driver.getState(),
  });
  const autosave = startAutosaveLoop(getSavedGame);
  // Expose autosave.saveNow on the __su dev hook for playtest convenience.
  (win.__su as Record<string, unknown>)['autosaveNow'] = () => autosave.saveNow();

  // In-game menu handle + sub-modal handles. Non-null while shown.
  let gameMenu: InGameMenuHandle | null = null;
  let gameMenuSavePicker: SaveSlotPickerHandle | null = null;
  let gameMenuLoadPicker: SaveSlotPickerHandle | null = null;
  let gameMenuSettings: SettingsModalHandle | null = null;
  // Speed captured when the menu opens; restored on Resume. Null when
  // the menu is closed.
  let speedBeforeMenu: SimSpeed | null = null;

  function closeGameMenuSubModals(): void {
    if (gameMenuSavePicker !== null) { gameMenuSavePicker.destroy(); gameMenuSavePicker = null; }
    if (gameMenuLoadPicker !== null) { gameMenuLoadPicker.destroy(); gameMenuLoadPicker = null; }
    if (gameMenuSettings !== null) { gameMenuSettings.destroy(); gameMenuSettings = null; }
  }

  function closeGameMenu(): void {
    closeGameMenuSubModals();
    if (gameMenu !== null) {
      gameMenu.destroy();
      gameMenu = null;
    }
    if (speedBeforeMenu !== null) {
      driver.setSpeed(speedBeforeMenu);
      speedBeforeMenu = null;
    }
  }

  function openGameMenu(): void {
    if (gameMenu !== null) return; // Already open.
    speedBeforeMenu = driver.getSpeed();
    driver.setSpeed(0);
    gameMenu = mountInGameMenu(document.body, {
      onResume: () => closeGameMenu(),
      onOpenSavePicker: () => {
        if (gameMenuSavePicker !== null) return;
        gameMenuSavePicker = mountSaveSlotPicker(document.body, 'save', {
          onSaveToSlot: (id, name) => {
            try {
              writeSlot(id, getSavedGame(), name);
            } catch (err) {
              // eslint-disable-next-line no-console
              console.warn(`[SU] save failed: ${err instanceof Error ? err.message : String(err)}`);
              window.alert(`Save failed: ${err instanceof Error ? err.message : String(err)}`);
              return;
            }
            gameMenuSavePicker?.refresh();
          },
          onDeleteSlot: (id) => {
            deleteSlot(id);
            gameMenuSavePicker?.refresh();
          },
          onCancel: () => {
            if (gameMenuSavePicker !== null) {
              gameMenuSavePicker.destroy();
              gameMenuSavePicker = null;
            }
          },
        });
      },
      onOpenLoadPicker: () => {
        if (gameMenuLoadPicker !== null) return;
        gameMenuLoadPicker = mountSaveSlotPicker(document.body, 'load', {
          onLoadFromSlot: (id) => {
            const saved = readSlot(id);
            if (saved === null) {
              window.alert(`Load failed — save "${id}" is missing or incompatible.`);
              return;
            }
            // Stash + reload — see loadInPlaceViaReload docs.
            loadInPlaceViaReload(saved);
          },
          onDeleteSlot: (id) => {
            deleteSlot(id);
            gameMenuLoadPicker?.refresh();
          },
          onCancel: () => {
            if (gameMenuLoadPicker !== null) {
              gameMenuLoadPicker.destroy();
              gameMenuLoadPicker = null;
            }
          },
        });
      },
      onOpenSettings: () => {
        if (gameMenuSettings !== null) return;
        gameMenuSettings = mountSettingsModal(document.body, {
          onClose: () => {
            if (gameMenuSettings !== null) {
              gameMenuSettings.destroy();
              gameMenuSettings = null;
            }
          },
        });
      },
      onReturnToMain: () => {
        // Ensure we don't stash a pending-load from a previous flow.
        sessionStorage.removeItem(PENDING_LOAD_KEY);
        window.location.reload();
      },
      onExit: () => exitGame(),
    });
  }

  // Space keybinding — pause / resume toggle.
  // MFix2.3 — Tab keybinding: toggle battle-view (SG system/galaxy pattern).
  //   If a battle is currently open, Tab closes it (returns to strategic map).
  //   If no battle is open but at least one battle exists, Tab opens the
  //   first battle (by id) that the player has a cohort in. If the player
  //   has no cohorts in any battle, Tab is a no-op — nothing to focus.
  // v0M15.4b (2026-07-30) — pause key is rebindable. Cache the current
  // binding + refresh via subscribeSettings so the handler stays in
  // sync when Michael changes it in the Settings modal.
  let bindPause = loadSettings().keybindings.pause.code;
  subscribeSettings((s) => { bindPause = s.keybindings.pause.code; });
  const onKey = (ev: KeyboardEvent) => {
    if (ev.code === bindPause) {
      // Avoid page-scroll on Space (or whatever is bound).
      ev.preventDefault();
      driver.togglePause();
    } else if (ev.code === 'KeyA') {
      // v0M4.2.5 — Assault city keybinding. If the currently selected
      // army holds an open siege on some city, issue an assault_settlement
      // command; the sim spawns a siege battle via
      // `createSiegeBattleFromArmies`. Silent no-op if the selected
      // army has no active siege (feedback deferred to a MFix polish
      // pass — see PLAYTEST-OPEN candidates).
      if (playerFactionId === null || selection.selectedArmyId === null) return;
      const state = driver.getState();
      const army = state.armies.get(selection.selectedArmyId);
      if (!army) return;
      // Check if this army holds any open siege.
      let hasOpenSiege = false;
      for (const city of state.settlements.values()) {
        if (city.siegeState !== null && city.siegeState.siegingArmyId === army.id) {
          hasOpenSiege = true;
          break;
        }
      }
      if (!hasOpenSiege) return;
      driver.enqueue({
        type: 'assault_settlement',
        tick: state.tick,
        playerFactionId,
        armyId: army.id,
      });
    } else if (ev.code === 'Tab') {
      ev.preventDefault();
      if (selection.openBattleCommandBattleId !== null) {
        // Close current battle-view.
        if (playerFactionId !== null) {
          driver.enqueue({
            type: 'exit_battle_command',
            tick: driver.getState().tick,
            playerFactionId,
            battleId: selection.openBattleCommandBattleId,
          });
        }
        closeBattleCommand(selection);
      } else if (playerFactionId !== null) {
        // Open the first player-participating battle.
        const state = driver.getState();
        const [firstOwnedBattle] = [...state.battles.values()].filter((b) => {
          for (const side of b.sides) {
            for (const cof of side.cohorts.values()) {
              if (cof.factionId === playerFactionId) return true;
            }
          }
          return false;
        });
        if (firstOwnedBattle !== undefined) {
          // MFix8.4: auto-close the Army Info panel when the player
          // enters battle command — the strategic-map panel is no
          // longer relevant while the battle modal is up.
          clearArmySelection(selection);
          openBattleCommand(selection, firstOwnedBattle.id);
          driver.enqueue({
            type: 'enter_battle_command',
            tick: driver.getState().tick,
            playerFactionId,
            battleId: firstOwnedBattle.id,
          });
        }
      }
    } else if (ev.code === 'KeyK') {
      // v0M9.5 (2026-07-24) — Knowledge modal hotkey. Toggle. Ignore
      // when typing into an input element (defensive; no inputs on the
      // main HUD today, but the dev console fits the shape).
      const active = document.activeElement;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return;
      if (selection.knowledgePanelOpen) {
        closeKnowledgePanel(selection);
      } else {
        openKnowledgePanel(selection);
      }
    }
  };
  window.addEventListener('keydown', onKey);

  // v0M7.5b (2026-07-26) — commit the accumulated Road polyline into a
  // set_army_build_order dispatch. Runs pathfind between adjacent
  // waypoints to expand the polyline into a full per-cell list; the
  // first cell becomes `targetCell` and the rest goes on `roadPath`.
  // Silently no-op if there are <2 waypoints (single waypoint =
  // single-cell road, still valid; 0 waypoints = cancel).
  function commitPendingRoadPolyline(): void {
    if (pendingRoadPolyline === null || playerFactionId === null) return;
    const { armyId, waypoints } = pendingRoadPolyline;
    pendingRoadPolyline = null;
    canvas.style.cursor = '';
    if (waypoints.length === 0) return;
    // Build the full cell list by pathing between each adjacent
    // waypoint pair. Deduplicate consecutive duplicates.
    const cells: { x: number; y: number }[] = [];
    if (waypoints.length === 1) {
      cells.push(waypoints[0]!);
    } else {
      const state = driver.getState();
      const bridges = computeBridgeCells(state, context.terrain.width);
      for (let i = 0; i < waypoints.length - 1; i++) {
        const a = waypoints[i]!;
        const b = waypoints[i + 1]!;
        const path = hierarchicalPath(
          context.terrain,
          context.regionGraph,
          a.x,
          a.y,
          b.x,
          b.y,
          bridges,
          WORLD_BRIDGE_MOVE_MULTIPLIER,
        );
        if (!path.found) return; // Abort commit if any segment unroutable.
        // path.cells is [start, ..., end]; skip the start on segments
        // after the first (it's the previous segment's end).
        const segCells = path.cells;
        const startIdx = i === 0 ? 0 : 1;
        for (let j = startIdx; j < segCells.length; j++) {
          const [cx, cy] = segCells[j]!;
          const lastPushed = cells[cells.length - 1];
          if (!lastPushed || lastPushed.x !== cx || lastPushed.y !== cy) {
            cells.push({ x: cx, y: cy });
          }
        }
      }
    }
    if (cells.length === 0) return;
    const targetCell = cells[0]!;
    const roadPath = cells.slice(1);
    driver.enqueue({
      type: 'set_army_build_order',
      tick: driver.getState().tick,
      playerFactionId,
      armyId,
      kind: 'road',
      targetCell,
      roadPath: roadPath.length > 0 ? roadPath : undefined,
    });
  }

  const { input } = mountInput(canvas, camera, {
    onLeftClick: (sx, sy) => {
      if (playerFactionId === null) return;
      const state = driver.getState();
      // v0M7.5 (2026-07-25) — location-picker mode consumes the click
      // FIRST. If a build order is pending, translate the click to a
      // cell + dispatch set_army_build_order + clear pending state.
      // Rest of the priority chain is skipped for this click.
      if (pendingBuildOrder !== null) {
        const cell = screenToCell(camera, canvas.clientWidth, canvas.clientHeight, sx, sy, map.width, map.height);
        if (cell !== null) {
          driver.enqueue({
            type: 'set_army_build_order',
            tick: state.tick,
            playerFactionId,
            armyId: pendingBuildOrder.armyId,
            kind: pendingBuildOrder.kind,
            targetCell: { x: cell.x, y: cell.y },
          });
        }
        pendingBuildOrder = null;
        canvas.style.cursor = '';
        return;
      }
      // v0M7.5b (2026-07-26) — Road polyline picker. LMB appends a
      // waypoint. RMB/Enter commit. Escape cancels.
      if (pendingRoadPolyline !== null) {
        const cell = screenToCell(camera, canvas.clientWidth, canvas.clientHeight, sx, sy, map.width, map.height);
        if (cell !== null) {
          // Skip duplicate consecutive waypoint.
          const last = pendingRoadPolyline.waypoints[pendingRoadPolyline.waypoints.length - 1];
          if (!last || last.x !== cell.x || last.y !== cell.y) {
            pendingRoadPolyline.waypoints.push({ x: cell.x, y: cell.y });
          }
        }
        return;
      }
      // Priority: battle icon > siege icon > army > city.
      const battleHit = findBattleAtScreen(state, camera, canvas.clientWidth, canvas.clientHeight, sx, sy, playerFactionId);
      if (battleHit !== null) {
        // MFix8.4: auto-close the Army Info panel on battle entry.
        clearArmySelection(selection);
        openBattleCommand(selection, battleHit);
        driver.enqueue({
          type: 'enter_battle_command',
          tick: state.tick,
          playerFactionId,
          battleId: battleHit,
        });
        return;
      }
      // MFix5.4 (2026-07-22): siege-icon click → open Command Siege
      // menu (Assault | Continue Siege).
      const siegeHit = findSiegeAtScreen(state, camera, canvas.clientWidth, canvas.clientHeight, sx, sy, playerFactionId);
      if (siegeHit !== null) {
        // Close any existing menu first so we don't stack.
        if (siegeMenu !== null) { siegeMenu.close(); siegeMenu = null; }
        siegeMenu = openSiegeCommandMenu({
          container: document.body,
          state,
          settlementId: siegeHit,
          playerFactionId,
          screenX: sx,
          screenY: sy,
          enqueue: (cmd) => driver.enqueue(cmd),
          onClose: () => { if (siegeMenu !== null) { siegeMenu.close(); siegeMenu = null; } },
        });
        return;
      }
      const armyHit = findArmyAtScreen(state, camera, canvas.clientWidth, canvas.clientHeight, sx, sy, playerFactionId);
      if (armyHit !== null) {
        selectArmy(selection, armyHit);
        closeCityPanel(selection);
        return;
      }
      // v0M5.3 (2026-07-25) — Spy hit-test. Falls after army so that if
      // a spy and an army sit on the same cell, the army takes selection
      // priority (matches shield-over-diamond render order).
      const spyHit = findSpyAtScreen(state, camera, canvas.clientWidth, canvas.clientHeight, sx, sy, playerFactionId);
      if (spyHit !== null) {
        selectSpy(selection, spyHit);
        closeCityPanel(selection);
        return;
      }
      // v0M5.4 (2026-07-25) — Check if the click landed on a settlement
      // (any faction's) where the player has a spy inside. If yes, open
      // the infiltration panel. Takes priority over the own-city panel
      // path so a settlement with both own ownership + own spy inside
      // opens the infiltration view (the player can still see build
      // queues etc. from the read-only Level 2/3 gates).
      const anySettlementHit = findAnySettlementAtScreen(state, map, camera, canvas.clientWidth, canvas.clientHeight, sx, sy);
      if (anySettlementHit !== null && hasOwnSpyInside(state, anySettlementHit, playerFactionId)) {
        openInfiltrationPanel(selection, anySettlementHit);
        return;
      }
      // v0M9 (2026-07-27) — check for owned minor settlement first;
      // clicks on minors open the pillage/repair panel, not the province
      // (capital) settlement panel.
      const minorHit = findMinorSettlementAtScreen(state, camera, canvas.clientWidth, canvas.clientHeight, sx, sy, playerFactionId);
      if (minorHit !== null) {
        openMinorSettlementPanel(selection, minorHit);
        closeCityPanel(selection);
        return;
      }
      const cityHit = findCityProvinceAtScreen(state, map, camera, canvas.clientWidth, canvas.clientHeight, sx, sy, playerFactionId);
      if (cityHit !== null) {
        openCityPanel(selection, cityHit);
        return;
      }
      // v0M7.4d+garrison.2 (2026-07-27) — check for owned Fort last
      // (smaller icon; let bigger targets win overlap). Opens the
      // Fort recruit panel.
      const fortHit = findFortAtScreen(state, camera, canvas.clientWidth, canvas.clientHeight, sx, sy, playerFactionId);
      if (fortHit !== null) {
        openFortPanel(selection, fortHit);
        return;
      }
      // Empty click — deselect both.
      clearArmySelection(selection);
      clearSpySelection(selection);
    },
    onRightClick: (sx, sy, shift) => {
      if (playerFactionId === null) return;
      // v0M7.5b (2026-07-26) — if a Road polyline is being drawn, RMB
      // commits it (pathfind between adjacent waypoints, dispatch as a
      // single set_army_build_order). Intercept before any spy / army
      // order handling so the polyline mode owns RMB exclusively.
      if (pendingRoadPolyline !== null) {
        commitPendingRoadPolyline();
        return;
      }
      // v0M5.3 — Spy move (checked first so a selected spy takes priority
      // over a selected army; single-selection UI means only one is set).
      // v0M5.4 — if the RMB lands on a settlement, dispatch
      // enter_settlement_spy instead of move_spy so the spy enters that
      // settlement (adjacency check enforced sim-side).
      // MFix11.2+.3 (2026-07-27) — RMB now dispatches
      // `set_spy_enter_target`: spy escorts to the closest adjacent cell
      // and auto-enters on arrival (movement.ts::resolveSpyEnterOnArrival).
      // The per-frame `spy → inside` diff below auto-opens the
      // infiltration panel once the spy transitions inside.
      if (selection.selectedSpyId !== null) {
        const state = driver.getState();
        const settlementHit = findAnySettlementAtScreen(state, map, camera, canvas.clientWidth, canvas.clientHeight, sx, sy);
        if (settlementHit !== null) {
          driver.enqueue({
            type: 'set_spy_enter_target',
            tick: state.tick,
            playerFactionId,
            spyId: selection.selectedSpyId,
            settlementId: settlementHit,
          });
          return;
        }
        const cell = screenToCell(camera, canvas.clientWidth, canvas.clientHeight, sx, sy, map.width, map.height);
        if (cell === null) return;
        driver.enqueue({
          type: 'move_spy',
          tick: state.tick,
          playerFactionId,
          spyId: selection.selectedSpyId,
          targetX: cell.x,
          targetY: cell.y,
          // MFix12.4 (2026-07-27) — shift+RMB queues the target after
          // the current movementGoal; plain RMB replaces the queue.
          append: shift,
        });
        return;
      }
      if (selection.selectedArmyId === null) return;
      const cell = screenToCell(camera, canvas.clientWidth, canvas.clientHeight, sx, sy, map.width, map.height);
      if (cell === null) return;
      driver.enqueue({
        type: 'move_army',
        tick: driver.getState().tick,
        playerFactionId,
        armyId: selection.selectedArmyId,
        targetX: cell.x,
        targetY: cell.y,
        // MFix12.4 (2026-07-27) — shift+RMB queues the target after
        // the current movementGoal; plain RMB replaces the queue.
        append: shift,
      });
    },
    onEscape: () => {
      // v0M7.5 — Escape cancels a pending build-order pick before
      // clearing selection. Two-stage escape: first click cancels
      // the picker; a second Escape (with no picker active) then
      // clears the selection.
      // v0M7.5b (2026-07-26) — extended to also cancel an accumulating
      // Road polyline (same two-stage semantics).
      // v0M15.5 (2026-07-29) — final fallback: if nothing to cancel or
      // clear, open the in-game menu. Ordering matters: Escape while
      // the menu is already up is handled by the menu's own listener
      // (mounted after this, on window). We early-out here so the
      // menu's Resume behaviour isn't shadowed by a selection-clear.
      if (gameMenu !== null) return;
      if (pendingBuildOrder !== null) {
        pendingBuildOrder = null;
        canvas.style.cursor = '';
        return;
      }
      if (pendingRoadPolyline !== null) {
        pendingRoadPolyline = null;
        canvas.style.cursor = '';
        return;
      }
      // Anything selectable / any panel open counts as "escape work
      // remaining" — clear it first before opening the menu on a
      // subsequent Escape.
      const hasSelectionOrPanel =
        selection.selectedArmyId !== null ||
        selection.selectedSpyId !== null ||
        selection.openPanelProvinceId !== null ||
        selection.openBattleCommandBattleId !== null ||
        selection.openInfiltrationSettlementId !== null ||
        selection.openMinorSettlementId !== null ||
        selection.openFortId !== null ||
        selection.openTransferSourceArmyId !== null ||
        selection.knowledgePanelOpen;
      if (hasSelectionOrPanel) {
        clearSelection(selection);
        return;
      }
      openGameMenu();
    },
    onEnter: () => {
      // v0M7.5b (2026-07-26) — Enter commits an accumulating Road
      // polyline (mirrors RMB). Silently no-ops if no polyline is
      // in flight so it doesn't steal Enter from other UI later.
      if (pendingRoadPolyline !== null) {
        commitPendingRoadPolyline();
      }
    },
  });

  let baked = bakeAllLayers(map, camera, atlas, {
    state: driver.getState(),
    playerFactionId,
  }, riverAtlas);
  // MFix5+fix6 (2026-07-23): track ownership signature so the political
  // tint re-bakes when a province flips owner (siege capture, assault
  // capture). Pre-fix, rebake only fired on zoom change so a captured
  // province stayed the old owner's tint until the player zoomed.
  let bakedOwnershipSig = computeOwnershipSignature(driver.getState());
  let lastFrameTime = performance.now();

  // MFix11.2+.3 (2026-07-27) — Per-frame diff of every player-owned spy's
  // `insideSettlementId`. When the spy transitions from outside (null) to
  // inside (settlementId), auto-open the infiltration panel for that
  // settlement. This is the arrival-side of the escort-and-enter flow
  // dispatched by RMB (see `set_spy_enter_target` above). Also covers the
  // legacy direct `enter_settlement_spy` path — any inside-transition
  // now opens the panel, so the player gets feedback regardless of route.
  //
  // Map key = SpyId; value = the last observed insideSettlementId (or null).
  // Initialized empty; every spy's first observation seeds the map without
  // firing the panel-open path (unless they were already inside somewhere
  // — but that's fine, opens the panel and the player can close it).
  type SpyInsideMap = Map<import('../sim/state').SpyId, import('../sim/state').SettlementId | null>;
  const lastSpyInside: SpyInsideMap = new Map();

  function frame(now: number): void {
    const dtMs = now - lastFrameTime;
    const dtSec = Math.min(0.05, dtMs / 1000);
    lastFrameTime = now;

    // Tick the sim FIRST so this frame renders the freshest state.
    driver.tick(dtMs);
    const state = driver.getState();
    const renderState: RenderState = {
      state,
      playerFactionId,
      selectedArmyId: selection.selectedArmyId,
      selectedSpyId: selection.selectedSpyId,
      // v0M7.5b (2026-07-26) — pass the in-flight Road polyline (if any)
      // so the renderer draws the numbered-dot preview overlay.
      pendingRoadPolyline: pendingRoadPolyline !== null ? pendingRoadPolyline.waypoints : null,
    };

    // Camera + input
    applyPanFromInput(camera, input, canvas, dtSec);
    clampCameraToMap(camera, map.width, map.height);

    // MFix5+fix6: rebake on zoom change OR ownership flip.
    const currentOwnershipSig = computeOwnershipSignature(state);
    if (pxPerCell(camera) !== baked.bakedZoom || currentOwnershipSig !== bakedOwnershipSig) {
      baked = bakeAllLayers(map, camera, atlas, renderState, riverAtlas);
      bakedOwnershipSig = currentOwnershipSig;
    }

    drawFrame(ctx, map, camera, baked, renderState);

    // HUD updates — day = tick / TICKS_PER_GAME_DAY per MECHANICS.md §1.1
    // (MFix3.8: was 5 ticks/day, now 25).
    const day = Math.floor(state.tick / TICKS_PER_GAME_DAY);
    speedHud.update(driver.getSpeed(), day);
    resourceHud.update(state);
    settlementPanel.sync(state, selection.openPanelProvinceId, playerFactionId);
    // v0M9 — minor settlement panel per-frame refresh.
    minorSettlementPanel.sync(state, selection.openMinorSettlementId, playerFactionId);
    // v0M7.4d+garrison.3 — Fort panel per-frame refresh.
    fortPanel.sync(state, map, selection.openFortId, playerFactionId);
    // v0M5.4 — infiltration panel per-frame refresh (mirrors the
    // settlement panel pattern). Auto-closes when the spy exits or is
    // killed (findBestSpyInSettlement returns null → panel hides).
    infiltrationPanel.sync(state, selection.openInfiltrationSettlementId, playerFactionId);
    battleModal.sync(state, selection.openBattleCommandBattleId, playerFactionId);
    // v0M8 (2026-07-23) — Army info panel refresh.
    armyInfoPanel.sync(state, selection.selectedArmyId);
    // MFix12.5b (2026-07-27) — cohort transfer modal refresh.
    cohortTransferModal.sync(state, selection.openTransferSourceArmyId, playerFactionId);
    // v0M9.5 (2026-07-24) — Knowledge panel refresh.
    knowledgePanel.sync(state, selection.knowledgePanelOpen, playerFactionId);
    // MFix5.4 — refresh siege menu label + auto-close if the underlying
    // siege has ended (city captured, break condition, etc.).
    if (siegeMenu !== null) siegeMenu.update(state);
    // Victory check — sync banner, and if just shown, auto-pause the sim.
    const newlyShown = victoryBanner.sync(state.victoryResult, playerFactionId);
    if (newlyShown) driver.setSpeed(0);

    // MFix11.2+.3 — spy arrival detection. Iterate player-owned spies;
    // on a null → non-null insideSettlementId transition, auto-open the
    // infiltration panel for that settlement.
    if (playerFactionId !== null && state.spies) {
      for (const spy of state.spies.values()) {
        if (spy.ownerFactionId !== playerFactionId) continue;
        const curr = spy.insideSettlementId ?? null;
        const prev = lastSpyInside.has(spy.id) ? lastSpyInside.get(spy.id)! : null;
        if (prev === null && curr !== null) {
          openInfiltrationPanel(selection, curr);
        }
        lastSpyInside.set(spy.id, curr);
      }
    }

    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}

function fitCanvasToWindow(canvas: HTMLCanvasElement): void {
  // v0M2.4 keeps the backing store 1:1 with CSS pixels for coordinate
  // simplicity. High-DPI polish (DPR-scaled backing + ctx.setTransform)
  // is a v0M2.4a candidate.
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  canvas.style.width = `${window.innerWidth}px`;
  canvas.style.height = `${window.innerHeight}px`;
}

function fail(message: string): void {
  const err = document.getElementById(BOOTSTRAP_ERROR_ID);
  if (err) {
    err.style.display = 'flex';
    err.textContent = message;
  }
}

// ---------------------------------------------------------------------------
// v0M15.5 — In-game Load handoff via sessionStorage + page reload
// ---------------------------------------------------------------------------

/** sessionStorage key holding the SavedGame that bootstrap should auto-load. */
const PENDING_LOAD_KEY = 'su:pending-load';

/**
 * Read + remove the pending-load payload. Called once during bootstrap.
 * Returns null if nothing is pending or the payload fails to parse
 * (schema mismatch, corruption) — in that case, boot falls through to
 * the main menu.
 *
 * sessionStorage (not localStorage) so a fresh tab starts clean.
 */
function consumePendingLoad(): SavedGame | null {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(PENDING_LOAD_KEY);
  if (raw === null) return null;
  // Always clear before returning, even on failure, so a bad payload
  // doesn't loop the game into the same broken state on refresh.
  sessionStorage.removeItem(PENDING_LOAD_KEY);
  try {
    return deserialiseSavedGame(raw);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[SU] pending-load payload rejected: ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
}

/**
 * Stash a SavedGame for the next boot cycle to auto-load, then reload
 * the page. Used by the in-game menu's Load Game path — cleanest way to
 * tear down every stateful client module in-flight (driver, HUDs,
 * panels, event listeners) is a full reload.
 */
function loadInPlaceViaReload(saved: SavedGame): void {
  try {
    sessionStorage.setItem(PENDING_LOAD_KEY, serialiseSavedGame(saved));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[SU] failed to stage load handoff: ${err instanceof Error ? err.message : String(err)}`);
    return;
  }
  window.location.reload();
}

/**
 * Full-screen tear-down flow (matches main-menu Exit / MFix2.1). Best-
 * effort: browsers block `window.close()` unless the tab was
 * script-opened, so we fall through to about:blank.
 */
function exitGame(): void {
  if (document.fullscreenElement !== null) {
    document.exitFullscreen().catch(() => { /* ignore */ });
  }
  window.close();
  setTimeout(() => {
    window.location.replace('about:blank');
  }, 100);
}

bootstrap().catch((e: unknown) => {
  const msg = e instanceof Error ? e.message : String(e);
  fail(`Bootstrap error: ${msg}`);
  // eslint-disable-next-line no-console
  console.error('[SU] bootstrap failed:', e);
});
